import React, { useMemo, useState } from 'react';
import { Icon } from '../../../StockControl';
import { stockButtonTones } from '../stockUi';

const getNormalizedSheetStatus = (sheet = {}) => {
  const explicitStatus = String(sheet?.status || '').trim().toLowerCase();
  if (['waiting_assignment', 'assigned', 'in_progress', 'completed', 'reviewed', 'cancelled'].includes(explicitStatus)) {
    return explicitStatus;
  }
  if (sheet?.completedAt) return 'completed';
  if (sheet?.startedAt) return 'in_progress';
  if (String(sheet?.assignedControllerId || '').trim() || String(sheet?.assignedControllerDisplayNameSnapshot || '').trim()) {
    return 'assigned';
  }
  return 'waiting_assignment';
};

const getSheetSourceLabel = (sheet = {}) => {
  if (sheet?.sourceType === 'category_control') {
    return sheet?.sourceCategorySnapshot
      ? `Categoría · ${sheet.sourceCategorySnapshot}`
      : 'Control por categoría';
  }
  if (sheet?.sourceType === 'manual') return 'Manual';
  if (sheet?.sourceType === 'import_excel') return 'Excel';
  if (sheet?.sourceType === 'integration') return 'Integración';
  return 'Operativo';
};

const SHEET_STATUS_STYLES = {
  waiting_assignment: 'bg-amber-100 text-amber-700',
  assigned: 'bg-cyan-100 text-cyan-700',
  in_progress: 'bg-violet-100 text-violet-700',
  completed: 'bg-emerald-100 text-emerald-700',
  reviewed: 'bg-slate-200 text-slate-700',
};

const formatSheetStatusLabel = (status) => {
  if (status === 'waiting_assignment') return 'Esperando asignación';
  if (status === 'assigned') return 'Asignado';
  if (status === 'in_progress') return 'En curso';
  if (status === 'completed') return 'Completado';
  return 'Revisada';
};

const StockSheetsPanel = ({
  sheets,
  controllers,
  products,
  form,
  setForm,
  onCreate,
  onAssign,
  onRelease,
  onAdvanceStatus,
  onStartControl,
  onOpenSheet,
}) => {
  const [assignmentMap, setAssignmentMap] = useState({});
  const activeControllers = useMemo(() => controllers.filter((item) => item.active !== false), [controllers]);
  const normalizedSheets = useMemo(
    () => sheets.map((sheet) => ({ ...sheet, status: getNormalizedSheetStatus(sheet) })),
    [sheets],
  );
  const activeSheets = useMemo(
    () => normalizedSheets.filter((sheet) => !['completed', 'reviewed'].includes(sheet.status)),
    [normalizedSheets],
  );
  const historicalSheets = useMemo(
    () => normalizedSheets
      .filter((sheet) => ['completed', 'reviewed'].includes(sheet.status))
      .sort((a, b) => {
        const aTime = a.completedAt?.seconds || a.updatedAt?.seconds || a.createdAt?.seconds || 0;
        const bTime = b.completedAt?.seconds || b.updatedAt?.seconds || b.createdAt?.seconds || 0;
        return bTime - aTime;
      }),
    [normalizedSheets],
  );
  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-600">Controles físicos</p>
            <h3 className="mt-1 text-xl font-bold text-slate-900">Cabecera operativa del control físico</h3>
            <p className="mt-1 text-sm text-slate-500">Desde acá administrás controles manuales o importados. La planilla sigue viva, pero queda dentro del carril correcto: control físico, asignación y ejecución.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center text-xs font-semibold text-slate-500 md:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2"><p>Totales</p><p className="text-lg text-slate-900">{normalizedSheets.length}</p></div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2"><p>Esperando</p><p className="text-lg text-slate-900">{normalizedSheets.filter((item) => item.status === 'waiting_assignment').length}</p></div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2"><p>Asignados</p><p className="text-lg text-slate-900">{normalizedSheets.filter((item) => item.status === 'assigned').length}</p></div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2"><p>En curso</p><p className="text-lg text-slate-900">{normalizedSheets.filter((item) => item.status === 'in_progress').length}</p></div>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <label className="space-y-1 md:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Título del control</span>
            <input
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-violet-400 focus:bg-white"
              placeholder="Control semanal depósito central"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Origen</span>
            <select
              value={form.sourceType}
              onChange={(e) => setForm((prev) => ({ ...prev, sourceType: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-violet-400 focus:bg-white"
            >
              <option value="manual">Manual</option>
              <option value="import_excel">Importación Excel</option>
              <option value="integration">Integración</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fecha operativa</span>
            <input
              type="date"
              value={form.sheetDate}
              onChange={(e) => setForm((prev) => ({ ...prev, sheetDate: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-violet-400 focus:bg-white"
            />
          </label>
          <label className="space-y-1 md:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Observación del control</span>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-violet-400 focus:bg-white"
              placeholder="Sector, depósito, referencia interna o comentario breve"
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={form.includeDayTasks}
              onChange={(e) => setForm((prev) => ({ ...prev, includeDayTasks: e.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
            />
            Generar líneas desde tareas del día elegido
          </label>
          <button
            onClick={onCreate}
            className={stockButtonTones.violet}
          >
            <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-5 w-5" />
            Nuevo control manual
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Cola operativa</p>
            <h4 className="mt-1 text-lg font-bold text-slate-900">Controles físicos registrados</h4>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{normalizedSheets.length} controles</span>
        </div>

        <div className="mt-4 space-y-3">
          {activeSheets.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              No hay controles activos en esta cola.
            </div>
          ) : activeSheets.map((sheet) => {
            const currentValue = assignmentMap[sheet.id] ?? sheet.assignedControllerId ?? '';
            return (
              <div key={sheet.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-base font-semibold text-slate-900">{sheet.title}</p>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${SHEET_STATUS_STYLES[sheet.status] || 'bg-slate-200 text-slate-600'}`}>
                        {formatSheetStatusLabel(sheet.status)}
                      </span>
                      <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                        {getSheetSourceLabel(sheet)}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                      <span>{sheet.sheetDate || 'Sin fecha'}</span>
                      <span>{sheet.linesCount || 0} líneas</span>
                      {sheet.assignedControllerDisplayNameSnapshot ? <span>Asignado a {sheet.assignedControllerDisplayNameSnapshot}</span> : <span>Sin controlador</span>}
                    </div>
                    {sheet.sourceType === 'category_control' && sheet.sourceCategorySnapshot ? (
                      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-violet-600">
                        Categoría controlada: {sheet.sourceCategorySnapshot}
                      </p>
                    ) : null}
                    {sheet.notes ? <p className="mt-2 text-sm text-slate-600">{sheet.notes}</p> : null}
                  </div>

                  <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto] lg:min-w-[380px]">
                    <select
                      value={currentValue}
                      onChange={(e) => setAssignmentMap((prev) => ({ ...prev, [sheet.id]: e.target.value }))}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-cyan-400"
                    >
                      <option value="">Seleccionar controlador</option>
                      {activeControllers.map((controller) => (
                        <option key={controller.id} value={controller.id}>
                          {[controller.firstName, controller.lastName].filter(Boolean).join(' ')}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => onAssign(sheet, currentValue)}
                      className={stockButtonTones.cyan}
                    >
                      Asignar
                    </button>
                    <button
                      onClick={() => onRelease(sheet)}
                      className={stockButtonTones.ghost}
                    >
                      Liberar
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => onOpenSheet?.(sheet)} className={stockButtonTones.softViolet}>Ver planilla</button>
                  <button onClick={() => onAdvanceStatus(sheet, 'in_progress')} className={stockButtonTones.softViolet}>Marcar en curso</button>
                  <button onClick={() => onAdvanceStatus(sheet, 'completed')} className={stockButtonTones.softEmerald}>Marcar completada</button>
                  <button onClick={() => onAdvanceStatus(sheet, 'reviewed')} className={stockButtonTones.ghost}>Marcar revisada</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Historial general</p>
            <h4 className="mt-1 text-lg font-bold text-slate-900">Controles finalizados y revisados</h4>
          </div>
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">{historicalSheets.length} cerrados</span>
        </div>

        <div className="mt-4 space-y-3">
          {historicalSheets.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              Todavía no hay controles cerrados para revisar.
            </div>
          ) : historicalSheets.map((sheet) => (
            <div key={sheet.id} className="rounded-2xl border border-emerald-100 bg-emerald-50/60 px-4 py-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-base font-semibold text-slate-900">{sheet.title}</p>
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${SHEET_STATUS_STYLES[sheet.status] || 'bg-slate-200 text-slate-600'}`}>
                      {formatSheetStatusLabel(sheet.status)}
                    </span>
                    <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                      {getSheetSourceLabel(sheet)}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                    <span>{sheet.sheetDate || 'Sin fecha'}</span>
                    <span>{sheet.linesCount || 0} líneas</span>
                    <span>{sheet.reviewedLinesCount || 0} revisadas</span>
                    <span>{sheet.differenceLinesCount || 0} con diferencia</span>
                  </div>
                  {sheet.assignedControllerDisplayNameSnapshot ? (
                    <p className="mt-2 text-sm text-slate-600">
                      Controlado por {sheet.assignedControllerDisplayNameSnapshot}
                    </p>
                  ) : null}
                  {sheet.notes ? <p className="mt-2 text-sm text-slate-600">{sheet.notes}</p> : null}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button onClick={() => onOpenSheet?.(sheet)} className={stockButtonTones.softViolet}>Ver planilla</button>
                  <button onClick={() => onAdvanceStatus(sheet, 'reviewed')} className={stockButtonTones.ghost}>Marcar revisada</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StockSheetsPanel;
